import React, { useState } from "react";
import styled from "styled-components";
import { FaFileAlt, FaDownload } from "react-icons/fa";

function getFileExtension(name = "", url = "") {
  const source = name || url;
  const match = source.split("?")[0].match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : "";
}

function FilePreview({ name, url }) {
  const [previewFailed, setPreviewFailed] = useState(false);
  const ext = getFileExtension(name, url);
  const imageExts = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"];
  const officeExts = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
  const textExts = ["txt", "csv", "md", "json", "log", "xml", "yml", "yaml"];
  const isImage = imageExts.includes(ext);
  const isPdf = ext === "pdf";
  const isOffice = officeExts.includes(ext);
  const isText = textExts.includes(ext);

  if (!url) return null;

  if (previewFailed) {
    return (
      <FallbackBox>
        ⚠️ Preview not available for this file type.
        <br />
        <a href={url} target="_blank" rel="noopener noreferrer">
          Open file in new tab
        </a>
      </FallbackBox>
    );
  }

  if (isImage) {
    return (
      <Section>
        <Label>Preview</Label>
        <PreviewImage
          src={url}
          alt={name}
          onError={() => setPreviewFailed(true)}
        />
      </Section>
    );
  }

  if (isPdf) {
    return (
      <Section>
        <Label>Preview</Label>
        <StyledIframe
          src={url}
          title="File Preview"
          width="100%"
          height="600"
          onError={() => setPreviewFailed(true)}
        />
      </Section>
    );
  }

  if (isOffice) {
    const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    return (
      <Section>
        <Label>Preview</Label>
        <StyledIframe
          src={googleViewerUrl}
          title="Office Document Preview"
          width="100%"
          height="600"
          onError={() => setPreviewFailed(true)}
        />
        <PreviewHint>
          Rendered by Google Docs viewer. If the preview fails, click "Open file
          in new tab" above to download.
        </PreviewHint>
      </Section>
    );
  }

  if (isText) {
    return (
      <Section>
        <Label>Preview</Label>
        <StyledIframe
          src={url}
          title="Text File Preview"
          width="100%"
          height="600"
          onError={() => setPreviewFailed(true)}
        />
      </Section>
    );
  }

  return (
    <Section>
      <FallbackBox>
        Preview not available for <strong>.{ext || "this file type"}</strong>.
        <br />
        <a href={url} target="_blank" rel="noopener noreferrer">
          Open file in new tab
        </a>
      </FallbackBox>
    </Section>
  );
}

const FilePopupCard = ({ name, url, onClose }) => {
  const ext = getFileExtension(name, url);

  return (
    <ContentWrapper>
      <CloseButton onClick={onClose}>✕</CloseButton>
      <Card>
        <TitleRow>
          <Label>File</Label>
          <DownloadAnchor href={url} target="_blank" rel="noopener noreferrer" download>
            <FaDownload size={12} style={{ marginRight: 4 }} />
            Download
          </DownloadAnchor>
        </TitleRow>
        <Header>{name || "Untitled File"}</Header>

        <Section>
          <Label>File Info</Label>
          <MetaGrid>
            <MetaBox
              color={{ bg: "#fef3c7", border: "#fde68a", text: "#92400e", value: "#78350f" }}
            >
              <IconWrapper>
                <FaFileAlt size={24} />
              </IconWrapper>
              <MetaText color={{ text: "#92400e", value: "#78350f" }}>
                <p>File Name</p>
                <p style={{ fontSize: "1rem", wordBreak: "break-all" }}>{name || "—"}</p>
              </MetaText>
            </MetaBox>

            <MetaBox
              color={{ bg: "#ede9fe", border: "#ddd6fe", text: "#5b21b6", value: "#4c1d95" }}
            >
              <IconWrapper>
                <FaFileAlt size={24} />
              </IconWrapper>
              <MetaText color={{ text: "#5b21b6", value: "#4c1d95" }}>
                <p>File Type</p>
                <p>{ext ? ext.toUpperCase() : "Unknown"}</p>
              </MetaText>
            </MetaBox>
          </MetaGrid>
        </Section>

        <FilePreview name={name} url={url} />
      </Card>
    </ContentWrapper>
  );
};

export default FilePopupCard;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-top: 15px;
  padding-bottom: 15px;
  align-items: flex-start;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -8px;
  right: 0;
  background: transparent;
  border: none;
  font-size: 1.8rem;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  &:hover {
    color: #111827;
  }
`;

const Card = styled.div`
  background: white;
  width: 90%;
  padding: 2rem 2rem 1rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  margin: 2rem auto;
  text-align: left;
  border: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const DownloadAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0b99ff;
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  word-break: break-all;
`;

const Label = styled.small`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const MetaText = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "color",
})`
  p:first-child {
    font-size: 0.875rem;
    color: ${({ color }) => color.text};
    font-weight: 500;
  }
  p:last-child {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const PreviewHint = styled.p`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
`;

const FallbackBox = styled.div`
  padding: 1rem;
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  text-align: center;
  font-size: 0.95rem;
  a {
    color: #2563eb;
    text-decoration: underline;
  }
`;
